import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { includes, isNil, isObject, padStart, split, toLower, trim, isString } from 'lodash'
import notify from 'components/atoms/Notify'
import Modal from 'components/molecules/Modal'
import apiPatch, { apiPatchFormData } from 'lib/network/apiPatch'

/** HSL triple (globals.css style) or #hex → #rrggbb for color input */
function brandingValueToHex(val) {
  if (!val || !isString(val)) return '#808080'
  const v = trim(val)
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) {
    if (v.length === 4) {
      const r = v[1]
      const g = v[2]
      const b = v[3]
      return toLower(`#${r}${r}${g}${g}${b}${b}`)
    }
    return toLower(v)
  }
  const m = /^(\d{1,3})\s+(\d{1,3})%\s+(\d{1,3})%$/.exec(v)
  if (!m) return '#808080'
  const h = parseInt(m[1], 10) / 360
  const s = parseInt(m[2], 10) / 100
  const l = parseInt(m[3], 10) / 100
  let r
  let g
  let b
  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p, q, t) => {
      let tt = t
      if (tt < 0) tt += 1
      if (tt > 1) tt -= 1
      if (tt < 1 / 6) return p + (q - p) * 6 * tt
      if (tt < 1 / 2) return q
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
      return p
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }
  const toHex = x => {
    const n = Math.round(x * 255)
    return padStart(n.toString(16), 2, '0')
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** Defaults aligned with frontend/src/app/globals.css :root (light) */
const DEFAULT_BRANDING_PREVIEW = {
  background: '0 0% 100%',
  foreground: '220 15% 15%',
  primary: '217 91% 52%',
  primaryForeground: '0 0% 100%',
  secondary: '262 80% 60%',
  secondaryForeground: '0 0% 100%',
  accent: '210 100% 65%',
  accentForeground: '0 0% 100%',
  muted: '220 14% 96%',
  mutedForeground: '220 9% 46%',
  border: '220 13% 91%',
  ring: '217 91% 52%',
  destructive: '0 84.2% 60.2%',
  destructiveForeground: '210 40% 98%',
  gradientPrimary: 'linear-gradient(135deg, hsl(217 91% 52%) 0%, hsl(262 80% 60%) 100%)',
  buttonRadius: '0.875rem',
  fontFamilyPreset: 'inter',
}

/** Local preview stacks — family names must match `next/font` in frontend `lib/appFonts.ts` */
const BRANDING_FONT_STACK_BY_ID = {
  inter: "'Inter', ui-sans-serif, system-ui, sans-serif",
  poppins: "'Poppins', ui-sans-serif, system-ui, sans-serif",
  'dm-sans': "'DM Sans', ui-sans-serif, system-ui, sans-serif",
  'open-sans': "'Open Sans', ui-sans-serif, system-ui, sans-serif",
  montserrat: "'Montserrat', ui-sans-serif, system-ui, sans-serif",
  lato: "'Lato', ui-sans-serif, system-ui, sans-serif",
  outfit: "'Outfit', ui-sans-serif, system-ui, sans-serif",
  'noto-sans-arabic': "'Noto Sans Arabic', ui-sans-serif, system-ui, sans-serif",
  rubik: "'Rubik', ui-sans-serif, system-ui, sans-serif",
  nunito: "'Nunito', ui-sans-serif, system-ui, sans-serif",
  'nunito-sans': "'Nunito Sans', ui-sans-serif, system-ui, sans-serif",
  'work-sans': "'Work Sans', ui-sans-serif, system-ui, sans-serif",
  'source-sans-3': "'Source Sans 3', ui-sans-serif, system-ui, sans-serif",
  raleway: "'Raleway', ui-sans-serif, system-ui, sans-serif",
  ubuntu: "'Ubuntu', ui-sans-serif, system-ui, sans-serif",
  barlow: "'Barlow', ui-sans-serif, system-ui, sans-serif",
  manrope: "'Manrope', ui-sans-serif, system-ui, sans-serif",
  'plus-jakarta-sans': "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif",
  figtree: "'Figtree', ui-sans-serif, system-ui, sans-serif",
  lexend: "'Lexend', ui-sans-serif, system-ui, sans-serif",
  karla: "'Karla', ui-sans-serif, system-ui, sans-serif",
  mulish: "'Mulish', ui-sans-serif, system-ui, sans-serif",
  quicksand: "'Quicksand', ui-sans-serif, system-ui, sans-serif",
  'space-grotesk': "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
  sora: "'Sora', ui-sans-serif, system-ui, sans-serif",
  'ibm-plex-sans': "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
  'titillium-web': "'Titillium Web', ui-sans-serif, system-ui, sans-serif",
  cabin: "'Cabin', ui-sans-serif, system-ui, sans-serif",
  'varela-round': "'Varela Round', ui-sans-serif, system-ui, sans-serif",
  'be-vietnam-pro': "'Be Vietnam Pro', ui-sans-serif, system-ui, sans-serif",
  'public-sans': "'Public Sans', ui-sans-serif, system-ui, sans-serif",
  epilogue: "'Epilogue', ui-sans-serif, system-ui, sans-serif",
}

const BRANDING_FONT_FAMILY_OPTIONS = [
  { id: 'inter', labelKey: 'brandingFontPresetInter' },
  { id: 'poppins', labelKey: 'brandingFontPresetPoppins' },
  { id: 'dm-sans', labelKey: 'brandingFontPresetDmSans' },
  { id: 'open-sans', labelKey: 'brandingFontPresetOpenSans' },
  { id: 'montserrat', labelKey: 'brandingFontPresetMontserrat' },
  { id: 'lato', labelKey: 'brandingFontPresetLato' },
  { id: 'outfit', labelKey: 'brandingFontPresetOutfit' },
  { id: 'noto-sans-arabic', labelKey: 'brandingFontPresetNotoSansArabic' },
  { id: 'rubik', labelKey: 'brandingFontPresetRubik' },
  { id: 'nunito', labelKey: 'brandingFontPresetNunito' },
  { id: 'nunito-sans', labelKey: 'brandingFontPresetNunitoSans' },
  { id: 'work-sans', labelKey: 'brandingFontPresetWorkSans' },
  { id: 'source-sans-3', labelKey: 'brandingFontPresetSourceSans3' },
  { id: 'raleway', labelKey: 'brandingFontPresetRaleway' },
  { id: 'ubuntu', labelKey: 'brandingFontPresetUbuntu' },
  { id: 'barlow', labelKey: 'brandingFontPresetBarlow' },
  { id: 'manrope', labelKey: 'brandingFontPresetManrope' },
  { id: 'plus-jakarta-sans', labelKey: 'brandingFontPresetPlusJakartaSans' },
  { id: 'figtree', labelKey: 'brandingFontPresetFigtree' },
  { id: 'lexend', labelKey: 'brandingFontPresetLexend' },
  { id: 'karla', labelKey: 'brandingFontPresetKarla' },
  { id: 'mulish', labelKey: 'brandingFontPresetMulish' },
  { id: 'quicksand', labelKey: 'brandingFontPresetQuicksand' },
  { id: 'space-grotesk', labelKey: 'brandingFontPresetSpaceGrotesk' },
  { id: 'sora', labelKey: 'brandingFontPresetSora' },
  { id: 'ibm-plex-sans', labelKey: 'brandingFontPresetIbmPlexSans' },
  { id: 'titillium-web', labelKey: 'brandingFontPresetTitilliumWeb' },
  { id: 'cabin', labelKey: 'brandingFontPresetCabin' },
  { id: 'varela-round', labelKey: 'brandingFontPresetVarelaRound' },
  { id: 'be-vietnam-pro', labelKey: 'brandingFontPresetBeVietnamPro' },
  { id: 'public-sans', labelKey: 'brandingFontPresetPublicSans' },
  { id: 'epilogue', labelKey: 'brandingFontPresetEpilogue' },
]

function brandingColorInputHex(form, key) {
  const v = form[key]
  if (v == null || (isString(v) && trim(v) === '')) {
    return brandingValueToHex(DEFAULT_BRANDING_PREVIEW[key])
  }
  return brandingValueToHex(v)
}

const BRANDING_BUTTON_RADIUS_PRESETS = ['0.25rem', '0.375rem', '0.5rem', '0.625rem', '0.75rem', '0.875rem', '1rem', '1.125rem', '1.25rem', '1.5rem']

const BRANDING_GRADIENT_PRIMARY_PRESETS = [
  {
    value: 'linear-gradient(135deg, hsl(217 91% 52%) 0%, hsl(262 80% 60%) 100%)',
    labelKey: 'brandingGradientOpt1',
  },
  {
    value: 'linear-gradient(90deg, hsl(217 91% 52%) 0%, hsl(262 80% 60%) 100%)',
    labelKey: 'brandingGradientOpt2',
  },
  {
    value: 'linear-gradient(180deg, hsl(217 91% 52%) 0%, hsl(262 80% 60%) 100%)',
    labelKey: 'brandingGradientOpt3',
  },
  {
    value: 'linear-gradient(45deg, hsl(217 91% 52%) 0%, hsl(262 80% 60%) 100%)',
    labelKey: 'brandingGradientOpt4',
  },
  {
    value: 'linear-gradient(135deg, hsl(262 80% 60%) 0%, hsl(217 91% 52%) 100%)',
    labelKey: 'brandingGradientOpt5',
  },
  {
    value: 'linear-gradient(135deg, hsl(210 100% 65%) 0%, hsl(217 91% 52%) 100%)',
    labelKey: 'brandingGradientOpt6',
  },
  {
    value: 'linear-gradient(135deg, hsl(217 91% 52%) 0%, hsl(210 100% 65%) 100%)',
    labelKey: 'brandingGradientOpt7',
  },
  {
    value: 'linear-gradient(to right, hsl(217 91% 52%), hsl(262 80% 60%))',
    labelKey: 'brandingGradientOpt8',
  },
  {
    value: 'linear-gradient(135deg, hsl(217 91% 52%) 0%, hsl(262 80% 60%) 50%, hsl(210 100% 65%) 100%)',
    labelKey: 'brandingGradientOpt9',
  },
  {
    value: 'linear-gradient(135deg, hsl(262 80% 60%) 0%, hsl(210 100% 65%) 100%)',
    labelKey: 'brandingGradientOpt10',
  },
]

const GRADIENT_PRIMARY_PRESET_VALUES = BRANDING_GRADIENT_PRIMARY_PRESETS.map(p => p.value)

function hexToHslTriple(hex) {
  let h = trim(hex).replace(/^#/, '')
  if (h.length === 3) {
    h = split(h, '')
      .map(c => c + c)
      .join('')
  }
  if (h.length !== 6) return '0 0% 50%'
  const r = parseInt(h.slice(0, 2), 16) / 255
  const g = parseInt(h.slice(2, 4), 16) / 255
  const b = parseInt(h.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let hue = 0
  let sat = 0
  const light = (max + min) / 2
  if (max !== min) {
    const d = max - min
    sat = light > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        hue = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        hue = ((b - r) / d + 2) / 6
        break
      default:
        hue = ((r - g) / d + 4) / 6
    }
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`
}

function brandingColorToHslTriple(val, fallback) {
  if (val == null || !isString(val) || trim(val) === '') return fallback
  const v = trim(val)
  if (/^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/.test(v)) return v
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return hexToHslTriple(v)
  return fallback
}

function trimBrandingField(val) {
  if (val == null || !isString(val)) return ''
  return trim(val)
}

function buildBrandingPreviewCssVars(form) {
  const d = DEFAULT_BRANDING_PREVIEW
  const h = key => brandingColorToHslTriple(form[key], d[key])
  const gp = trimBrandingField(form.gradientPrimary)
  const rad = trimBrandingField(form.buttonRadius)
  const preset = trimBrandingField(form.fontFamilyPreset) || 'inter'
  const bodyFont = BRANDING_FONT_STACK_BY_ID[preset] || BRANDING_FONT_STACK_BY_ID.inter
  return {
    '--background': h('background'),
    '--foreground': h('foreground'),
    '--primary': h('primary'),
    '--primary-foreground': h('primaryForeground'),
    '--secondary': h('secondary'),
    '--secondary-foreground': h('secondaryForeground'),
    '--accent': h('accent'),
    '--accent-foreground': h('accentForeground'),
    '--muted': h('muted'),
    '--muted-foreground': h('mutedForeground'),
    '--border': h('border'),
    '--ring': h('ring'),
    '--destructive': h('destructive'),
    '--destructive-foreground': h('destructiveForeground'),
    '--card': '0 0% 100%',
    '--card-foreground': h('foreground'),
    '--gradient-primary': gp || d.gradientPrimary,
    '--radius': rad || d.buttonRadius,
    fontFamily: bodyFont,
  }
}

function BrandingLivePreview({ style, logoSrc, faviconSrc, companyName, t }) {
  const brand = companyName || 'Brand'
  const headingFont = style.fontFamily || BRANDING_FONT_STACK_BY_ID.inter
  const mergedStyle = {
    ...style,
    fontSize: '13px',
    lineHeight: 1.5,
  }
  return (
    <div className="relative">
      <div className="pointer-events-none absolute -inset-1 rounded-[1.35rem] bg-gradient-to-br from-indigo-400/25 via-violet-400/15 to-slate-400/25 blur-md" aria-hidden="true" />
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_25px_50px_-12px_rgba(15,23,42,0.15),0_0_0_1px_rgba(15,23,42,0.04)]">
        <div style={mergedStyle}>
          <div
            className="relative flex items-center gap-2.5 border-b border-slate-200/90 bg-gradient-to-b from-slate-100 to-slate-50/95 px-3 py-2.5"
            style={{ borderColor: 'hsl(var(--border))' }}
          >
            <span className="flex shrink-0 gap-1.5 pl-0.5" aria-hidden="true">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.15)]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.12)]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28c840] shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.12)]" />
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200/90 bg-white/95 px-2.5 py-1 text-[11px] font-medium text-slate-500 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.9)]">
              {faviconSrc ? (
                <img src={faviconSrc} alt="" className="h-3.5 w-3.5 shrink-0 rounded object-contain ring-1 ring-slate-200/80" />
              ) : (
                <span className="inline-flex h-3.5 w-3.5 shrink-0 rounded bg-slate-200/90 ring-1 ring-slate-300/40" />
              )}
              <span className="truncate">{t('companyProfile.brandingPreviewUrl')}</span>
            </div>
          </div>
          <div
            className="relative flex items-center justify-between gap-3 border-b px-3 py-3 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.9)] backdrop-blur-[2px]"
            style={{
              borderColor: 'hsl(var(--border))',
              backgroundColor: 'hsl(var(--background))',
              color: 'hsl(var(--foreground))',
            }}
          >
            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-200/80 to-transparent" aria-hidden="true" />
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              {logoSrc ? (
                <span
                  className="flex h-9 shrink-0 items-center justify-center rounded-lg bg-white/80 px-1.5 ring-1 ring-slate-200/80"
                  style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.06)' }}
                >
                  <img src={logoSrc} alt="" className="h-7 max-w-[112px] object-contain" />
                </span>
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400 ring-1 ring-slate-200/80">
                  {brand.slice(0, 2)}
                </span>
              )}
              <span className="truncate text-sm font-semibold tracking-tight" style={{ fontFamily: headingFont }}>
                {brand}
              </span>
            </div>
            <button
              type="button"
              className="shrink-0 px-3 py-1.5 text-xs font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-shadow hover:shadow-md"
              style={{
                background: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
                borderRadius: 'var(--radius)',
              }}
            >
              {t('companyProfile.brandingPreviewBook')}
            </button>
          </div>
          <div className="relative overflow-hidden">
            <div
              className="relative px-4 pb-8 pt-8 text-center sm:px-5 sm:pb-10 sm:pt-9"
              style={{
                background: 'var(--gradient-primary)',
              }}
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.22]"
                style={{
                  backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.35) 1px, transparent 0)',
                  backgroundSize: '20px 20px',
                }}
                aria-hidden="true"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/[0.12] via-transparent to-black/20" aria-hidden="true" />
              <div className="relative">
                <span
                  className="mb-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/95 ring-1 ring-white/20"
                  style={{
                    background: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  {t('companyProfile.brandingPreviewCardTitle')}
                </span>
                <p
                  className="text-[1.05rem] font-bold leading-snug tracking-tight sm:text-lg"
                  style={{
                    fontFamily: headingFont,
                    color: 'hsl(var(--primary-foreground))',
                    textShadow: '0 2px 24px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.15)',
                  }}
                >
                  {t('companyProfile.brandingPreviewHeroTitle')}
                </p>
                <p
                  className="mx-auto mt-2 max-w-[18rem] text-[11px] leading-relaxed sm:text-xs"
                  style={{
                    color: 'hsl(var(--primary-foreground))',
                    opacity: 0.92,
                    textShadow: '0 1px 12px rgba(0,0,0,0.12)',
                  }}
                >
                  {t('companyProfile.brandingPreviewHeroSubtitle')}
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-2 p-3 sm:p-3.5" style={{ backgroundColor: 'hsl(var(--muted))' }}>
            <div
              className="border p-3.5 shadow-[0_4px_14px_-4px_rgba(15,23,42,0.15),inset_0_1px_0_0_rgba(255,255,255,0.9)]"
              style={{
                borderColor: 'hsl(var(--border))',
                borderRadius: 'var(--radius)',
                backgroundColor: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
              }}
            >
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold" style={{ fontFamily: headingFont }}>
                  {t('companyProfile.brandingPreviewCardTitle')}
                </p>
                <span className="inline-flex h-2 w-2 shrink-0 rounded-full ring-2 ring-white/80" style={{ background: 'hsl(var(--accent))' }} title="accent" aria-hidden="true" />
              </div>
              <p className="mt-1.5 text-[11px] leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>
                {t('companyProfile.brandingPreviewMuted')}
              </p>
              <div className="mt-3.5 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-2.5 py-1.5 text-[11px] font-semibold shadow-sm"
                  style={{
                    background: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  {t('companyProfile.brandingPreviewBook')}
                </button>
                <button
                  type="button"
                  className="px-2.5 py-1.5 text-[11px] font-semibold shadow-sm"
                  style={{
                    background: 'hsl(var(--secondary))',
                    color: 'hsl(var(--secondary-foreground))',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  {t('companyProfile.brandingPreviewSecondary')}
                </button>
                <button
                  type="button"
                  className="border-2 border-transparent bg-transparent px-2.5 py-1.5 text-[11px] font-semibold"
                  style={{
                    borderColor: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary))',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  {t('companyProfile.brandingPreviewOutline')}
                </button>
                <span className="inline-flex items-center px-1 text-[11px] font-medium" style={{ color: 'hsl(var(--destructive))' }}>
                  {t('companyProfile.brandingPreviewDestructive')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const BRANDING_COLOR_FIELD_KEYS = [
  'primary',
  'primaryForeground',
  'secondary',
  'secondaryForeground',
  'accent',
  'accentForeground',
  'background',
  'foreground',
  'muted',
  'mutedForeground',
  'border',
  'ring',
  'destructive',
  'destructiveForeground',
]

/** Keys allowed by PATCH /company/:id/branding — excludes server fields like updatedAt, updatedBy */
const BRANDING_API_KEYS = [...BRANDING_COLOR_FIELD_KEYS, 'logoUrl', 'faviconUrl', 'buttonRadius', 'gradientPrimary', 'fontFamilyPreset']

const BRANDING_DEFAULTS_FOR_API = {
  ...DEFAULT_BRANDING_PREVIEW,
  logoUrl: null,
  faviconUrl: null,
}

/** Local form state for “reset to defaults” — same as API defaults (colors, radius, gradient, no assets). */
const DEFAULT_BRANDING_FORM_STATE = {
  ...DEFAULT_BRANDING_PREVIEW,
  logoUrl: null,
  faviconUrl: null,
}

function pickBrandingForApi(raw) {
  const source = isObject(raw) ? raw : {}
  const out = {}
  for (const key of BRANDING_API_KEYS) {
    const v = source[key]
    const def = BRANDING_DEFAULTS_FOR_API[key]
    if (!isNil(v) && !(isString(v) && trim(v) === '')) {
      out[key] = v
    } else if (!isNil(def)) {
      out[key] = def
    } else if (v === null || (isString(v) && trim(v) === '')) {
      out[key] = null
    }
  }
  return out
}

const CompanyBrandingTab = ({ companyId, companyData, refetch, dnsEditLocked = false }) => {
  const { t } = useTranslation()
  const [brandingForm, setBrandingForm] = useState({})
  const [brandingSaving, setBrandingSaving] = useState(false)
  const [brandingLogoFile, setBrandingLogoFile] = useState(null)
  const [brandingFaviconFile, setBrandingFaviconFile] = useState(null)
  const [brandingLogoPreviewUrl, setBrandingLogoPreviewUrl] = useState(null)
  const [brandingFaviconPreviewUrl, setBrandingFaviconPreviewUrl] = useState(null)
  const [showResetBrandingModal, setShowResetBrandingModal] = useState(false)
  const brandingLogoInputRef = useRef(null)
  const brandingFaviconInputRef = useRef(null)

  const brandingPreviewCssVars = useMemo(() => buildBrandingPreviewCssVars(brandingForm), [brandingForm])
  const brandingPreviewLogoSrc = brandingLogoPreviewUrl || brandingForm.logoUrl
  const brandingPreviewFaviconSrc = brandingFaviconPreviewUrl || brandingForm.faviconUrl

  useEffect(() => {
    if (!companyData) return
    setBrandingForm(pickBrandingForApi(companyData.branding))
  }, [companyData])

  useEffect(() => {
    return () => {
      if (brandingLogoPreviewUrl) URL.revokeObjectURL(brandingLogoPreviewUrl)
      if (brandingFaviconPreviewUrl) URL.revokeObjectURL(brandingFaviconPreviewUrl)
    }
  }, [brandingLogoPreviewUrl, brandingFaviconPreviewUrl])

  const clearBrandingFileInputs = () => {
    if (brandingLogoPreviewUrl) URL.revokeObjectURL(brandingLogoPreviewUrl)
    if (brandingFaviconPreviewUrl) URL.revokeObjectURL(brandingFaviconPreviewUrl)
    setBrandingLogoPreviewUrl(null)
    setBrandingFaviconPreviewUrl(null)
    setBrandingLogoFile(null)
    setBrandingFaviconFile(null)
    if (brandingLogoInputRef.current) brandingLogoInputRef.current.value = ''
    if (brandingFaviconInputRef.current) brandingFaviconInputRef.current.value = ''
  }

  const handleOpenResetBrandingModal = () => {
    setShowResetBrandingModal(true)
  }

  const handleConfirmResetBrandingPersist = async () => {
    setBrandingSaving(true)
    try {
      clearBrandingFileInputs()
      const brandingPayload = pickBrandingForApi(DEFAULT_BRANDING_FORM_STATE)
      const res = await apiPatch(`company/${companyId}/branding`, { branding: brandingPayload })
      if (res.success) {
        setShowResetBrandingModal(false)
        setBrandingForm({ ...DEFAULT_BRANDING_FORM_STATE })
        notify.success(t('companyProfile.brandingSaved'))
        refetch?.()
      }
    } finally {
      setBrandingSaving(false)
    }
  }

  const handleSaveBranding = async () => {
    setBrandingSaving(true)
    try {
      const brandingPayload = pickBrandingForApi(brandingForm)
      const hasFiles = Boolean(brandingLogoFile || brandingFaviconFile)
      let res
      if (hasFiles) {
        const fd = new FormData()
        fd.append('branding', JSON.stringify(brandingPayload))
        if (brandingLogoFile) fd.append('brandingLogo', brandingLogoFile)
        if (brandingFaviconFile) fd.append('brandingFavicon', brandingFaviconFile)
        res = await apiPatchFormData(`company/${companyId}/branding`, fd)
      } else {
        res = await apiPatch(`company/${companyId}/branding`, { branding: brandingPayload })
      }
      if (res.success) {
        notify.success(t('companyProfile.brandingSaved'))
        clearBrandingFileInputs()
        refetch?.()
      }
    } finally {
      setBrandingSaving(false)
    }
  }

  return (
    <div className="space-y-8 py-8 pb-10 border-t border-gray-200">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 p-6 shadow-[0_1px_0_0_rgba(15,23,42,0.04)] sm:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-indigo-400/15 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-12 -left-10 h-40 w-40 rounded-full bg-violet-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-indigo-600/90">{t('companyProfile.tabs.branding')}</p>
          <h3 className="mt-2 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{t('companyProfile.brandingIntroTitle')}</h3>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{t('companyProfile.brandingHelp')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(300px,480px)] lg:gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(320px,520px)] xl:gap-10 2xl:grid-cols-[minmax(0,1fr)_minmax(340px,560px)]">
        <div className="min-w-0 space-y-10">
          <div>
            <div className="mb-4 border-b border-slate-200/90 pb-3">
              <h4 className="text-base font-semibold text-slate-900">{t('companyProfile.brandingColors')}</h4>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {BRANDING_COLOR_FIELD_KEYS.map(key => (
                <div key={key} className="group flex min-w-0 flex-col gap-3 rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                  <div>
                    <h5 className="text-sm font-semibold text-slate-900">{t(`companyProfile.brandingColorLabels.${key}.title`)}</h5>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{t(`companyProfile.brandingColorLabels.${key}.description`)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      id={`branding-${key}`}
                      type="color"
                      className="h-12 w-14 shrink-0 cursor-pointer rounded-lg border border-slate-200/90 bg-white shadow-inner transition-transform group-hover:scale-[1.02]"
                      value={brandingColorInputHex(brandingForm, key)}
                      onChange={e => setBrandingForm({ ...brandingForm, [key]: e.target.value })}
                      aria-label={t(`companyProfile.brandingColorLabels.${key}.title`)}
                    />
                    <div className="min-w-0 flex-1 rounded-lg border border-slate-100 bg-slate-50/50 px-2.5 py-1.5">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{t('companyProfile.brandingHexLabel')}</p>
                      <p className="truncate font-mono text-xs font-medium text-slate-800">{brandingColorInputHex(brandingForm, key)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-4 border-b border-slate-200/90 pb-3 text-base font-semibold text-slate-900">{t('companyProfile.brandingTypography')}</h4>
            <p className="mb-4 text-xs leading-relaxed text-slate-500">{t('companyProfile.brandingTypographyHint')}</p>
            <div className="flex min-w-0 max-w-md flex-col gap-2">
              <label className="text-xs font-semibold text-slate-700" htmlFor="branding-font-family">
                {t('companyProfile.brandingFontFamily')}
              </label>
              <select
                id="branding-font-family"
                className="block h-10 w-full cursor-pointer rounded-md border border-gray-300 bg-white pl-3 pr-8 text-sm text-gray-900 shadow-sm focus:border-dark-500 focus:outline-none focus:ring-2 focus:ring-dark-500"
                value={brandingForm.fontFamilyPreset || 'inter'}
                onChange={e => setBrandingForm({ ...brandingForm, fontFamilyPreset: e.target.value })}
              >
                {BRANDING_FONT_FAMILY_OPTIONS.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {t(`companyProfile.${opt.labelKey}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <h4 className="mb-4 flex items-center gap-2.5 border-b border-slate-200/90 pb-3 text-base font-semibold text-slate-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-xs font-bold text-indigo-600 shadow-sm ring-1 ring-slate-200/80">Aa</span>
              {t('companyProfile.brandingAssets')}
            </h4>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-dashed border-slate-200/90 bg-slate-50/30 p-4">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('companyProfile.brandingLogo')}</span>
                {(brandingLogoPreviewUrl || brandingForm.logoUrl) && (
                  <div className="flex min-h-[5rem] items-center justify-center rounded-lg bg-white p-4 ring-1 ring-slate-200/80">
                    <img src={brandingLogoPreviewUrl || brandingForm.logoUrl} alt="" className="h-16 w-auto max-w-full object-contain" />
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={brandingLogoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    className="block w-full text-sm text-gray-600 file:mr-2 file:rounded-md file:border-0 file:bg-dark-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-dark-700"
                    onChange={e => {
                      const file = e.target.files?.[0]
                      if (brandingLogoPreviewUrl) URL.revokeObjectURL(brandingLogoPreviewUrl)
                      if (file) {
                        setBrandingLogoFile(file)
                        setBrandingLogoPreviewUrl(URL.createObjectURL(file))
                      } else {
                        setBrandingLogoFile(null)
                        setBrandingLogoPreviewUrl(null)
                      }
                    }}
                  />
                  {(brandingForm.logoUrl || brandingLogoPreviewUrl) && (
                    <button
                      type="button"
                      className="text-xs font-medium text-red-600 hover:text-red-800"
                      onClick={() => {
                        if (brandingLogoPreviewUrl) URL.revokeObjectURL(brandingLogoPreviewUrl)
                        setBrandingLogoPreviewUrl(null)
                        setBrandingLogoFile(null)
                        setBrandingForm(prev => ({ ...prev, logoUrl: null }))
                        if (brandingLogoInputRef.current) brandingLogoInputRef.current.value = ''
                      }}
                    >
                      {t('companyProfile.removeBrandingAsset')}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-dashed border-slate-200/90 bg-slate-50/30 p-4">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('companyProfile.brandingFavicon')}</span>
                {(brandingFaviconPreviewUrl || brandingForm.faviconUrl) && (
                  <div className="flex min-h-[5rem] items-center justify-center rounded-lg bg-white p-4 ring-1 ring-slate-200/80">
                    <img src={brandingFaviconPreviewUrl || brandingForm.faviconUrl} alt="" className="h-12 w-12 object-contain" />
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={brandingFaviconInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.ico,image/x-icon"
                    className="block w-full text-sm text-gray-600 file:mr-2 file:rounded-md file:border-0 file:bg-dark-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-dark-700"
                    onChange={e => {
                      const file = e.target.files?.[0]
                      if (brandingFaviconPreviewUrl) URL.revokeObjectURL(brandingFaviconPreviewUrl)
                      if (file) {
                        setBrandingFaviconFile(file)
                        setBrandingFaviconPreviewUrl(URL.createObjectURL(file))
                      } else {
                        setBrandingFaviconFile(null)
                        setBrandingFaviconPreviewUrl(null)
                      }
                    }}
                  />
                  {(brandingForm.faviconUrl || brandingFaviconPreviewUrl) && (
                    <button
                      type="button"
                      className="text-xs font-medium text-red-600 hover:text-red-800"
                      onClick={() => {
                        if (brandingFaviconPreviewUrl) URL.revokeObjectURL(brandingFaviconPreviewUrl)
                        setBrandingFaviconPreviewUrl(null)
                        setBrandingFaviconFile(null)
                        setBrandingForm(prev => ({ ...prev, faviconUrl: null }))
                        if (brandingFaviconInputRef.current) brandingFaviconInputRef.current.value = ''
                      }}
                    >
                      {t('companyProfile.removeBrandingAsset')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="mb-4 border-b border-slate-200/90 pb-3 text-base font-semibold text-slate-900">{t('companyProfile.brandingOther')}</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-2">
                <label className="text-xs font-semibold text-slate-700" htmlFor="branding-buttonRadius">
                  {t('companyProfile.brandingButtonRadius')}
                </label>
                <select
                  id="branding-buttonRadius"
                  className="block h-10 w-full cursor-pointer rounded-md border border-gray-300 bg-white pl-3 pr-8 text-sm text-gray-900 shadow-sm focus:border-dark-500 focus:outline-none focus:ring-2 focus:ring-dark-500"
                  value={brandingForm.buttonRadius ?? ''}
                  onChange={e => {
                    const v = e.target.value
                    setBrandingForm(prev => ({ ...prev, buttonRadius: v === '' ? null : v }))
                  }}
                >
                  <option value="">{t('companyProfile.brandingRadiusDefault')}</option>
                  {BRANDING_BUTTON_RADIUS_PRESETS.map(r => (
                    <option key={r} value={r}>
                      {r}
                      {r === '0.875rem' ? ` (${t('companyProfile.brandingRadiusRecommended')})` : ''}
                    </option>
                  ))}
                  {brandingForm.buttonRadius && !includes(BRANDING_BUTTON_RADIUS_PRESETS, brandingForm.buttonRadius) && (
                    <option value={brandingForm.buttonRadius}>{brandingForm.buttonRadius}</option>
                  )}
                </select>
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <label className="text-xs font-semibold text-slate-700" htmlFor="branding-gradientPrimary">
                  {t('companyProfile.brandingGradientPrimary')}
                </label>
                <select
                  id="branding-gradientPrimary"
                  className="block h-10 w-full cursor-pointer rounded-md border border-gray-300 bg-white pl-3 pr-8 text-sm text-gray-900 shadow-sm focus:border-dark-500 focus:outline-none focus:ring-2 focus:ring-dark-500"
                  value={trimBrandingField(brandingForm.gradientPrimary || '')}
                  onChange={e => {
                    const v = e.target.value
                    setBrandingForm(prev => ({ ...prev, gradientPrimary: v === '' ? null : v }))
                  }}
                >
                  <option value="">{t('companyProfile.brandingGradientDefault')}</option>
                  {BRANDING_GRADIENT_PRIMARY_PRESETS.map(p => (
                    <option key={p.labelKey} value={p.value}>
                      {t(`companyProfile.${p.labelKey}`)}
                    </option>
                  ))}
                  {(() => {
                    const g = trimBrandingField(brandingForm.gradientPrimary || '')
                    if (!g || includes(GRADIENT_PRIMARY_PRESET_VALUES, g)) return null
                    return (
                      <option value={g}>
                        {t('companyProfile.brandingGradientCustom')}: {g.length > 72 ? `${g.slice(0, 72)}…` : g}
                      </option>
                    )
                  })()}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-[min(420px,100vw-2rem)] space-y-4 self-start lg:z-20" style={{ position: 'sticky', top: '1.25rem' }}>
          <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-dark-600 text-white shadow-sm">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </span>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{t('companyProfile.brandingPreviewTitle')}</h4>
                  <p className="text-[11px] text-slate-500">{t('companyProfile.brandingPreviewHint')}</p>
                </div>
              </div>
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-600/20">
                <span className="mr-1.5 inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" aria-hidden="true" />
                {t('companyProfile.brandingPreviewLive')}
              </span>
            </div>
            <div className="mt-4">
              <BrandingLivePreview style={brandingPreviewCssVars} logoSrc={brandingPreviewLogoSrc} faviconSrc={brandingPreviewFaviconSrc} companyName={companyData?.name} t={t} />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showResetBrandingModal}
        onClose={() => {
          if (!brandingSaving) setShowResetBrandingModal(false)
        }}
        title={t('companyProfile.brandingResetModalTitle')}
        description={t('companyProfile.brandingResetModalDescription')}
        width={520}
        height="auto"
        closeable={!brandingSaving}
        padding="p-6"
        customClass="!h-auto !min-h-0 !overflow-visible"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-gray-600">{t('companyProfile.brandingResetModalBody')}</p>
          <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => setShowResetBrandingModal(false)}
              disabled={brandingSaving}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('companyProfile.brandingResetModalCancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirmResetBrandingPersist}
              disabled={brandingSaving}
              className="inline-flex items-center rounded-md border border-transparent bg-dark-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {brandingSaving ? t('companyProfile.saving') : t('companyProfile.brandingResetModalConfirm')}
            </button>
          </div>
        </div>
      </Modal>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200/90 pt-6">
        <button
          type="button"
          onClick={handleOpenResetBrandingModal}
          disabled={brandingSaving || dnsEditLocked}
          className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('companyProfile.brandingResetDefaults')}
        </button>
        <button
          type="button"
          onClick={handleSaveBranding}
          disabled={brandingSaving || dnsEditLocked}
          className="inline-flex items-center rounded-md border border-transparent bg-dark-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {brandingSaving ? t('companyProfile.saving') : t('companyProfile.saveBranding')}
        </button>
      </div>
    </div>
  )
}

export default CompanyBrandingTab
